import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Phone, Mail } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const gstServices = [
    { name: 'GST Registration', path: '/gst-services#registration' },
    { name: 'GST Return Filing', path: '/gst-services#return-filing' },
    { name: 'GST LUT Form', path: '/gst-services#lut-form' },
    { name: 'GST Audit', path: '/gst-services#audit' },
    { name: 'GST Revocation', path: '/gst-services#revocation' },
    { name: 'GST Amendment', path: '/gst-services#amendment' },
    { name: 'GSTR 10', path: '/gst-services#gstr10' },
    { name: 'GST Notice Reply', path: '/gst-services#notice-reply' },
    { name: 'GSTR 9C', path: '/gst-services#gstr9c' },
  ];

  const incomeTaxServices = [
    { name: 'Income Tax e-Filing', path: '/income-tax-services#efiling' },
    { name: 'ITR 1 Filing', path: '/income-tax-services#itr1' },
    { name: 'ITR 2 Filing', path: '/income-tax-services#itr2' },
    { name: 'ITR 3 Filing', path: '/income-tax-services#itr3' },
    { name: 'ITR 4 Filing', path: '/income-tax-services#itr4' },
    { name: 'ITR 5 Filing', path: '/income-tax-services#itr5' },
    { name: 'ITR 6 Filing', path: '/income-tax-services#itr6' },
    { name: 'ITR 7 Filing', path: '/income-tax-services#itr7' },
    { name: 'TAN Registration', path: '/income-tax-services#tan' },
    { name: 'TDS Return Filing', path: '/income-tax-services#tds' },
    { name: 'Income Tax Notice', path: '/income-tax-services#notice' },
    { name: 'Tax Calculator', path: '/income-tax-services#calculator' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/70 backdrop-blur-xl border-b border-slate-200/50 shadow-sm'
          : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      {/* Top Bar */}
      <div className="bg-[#2D3C92] text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center text-sm">
            <div className="flex items-center gap-4">
              <a
                href="tel:6268642514"
                className="flex items-center gap-2 hover:text-[#10B981] transition-colors"
                data-testid="header-phone-link"
              >
                <Phone size={14} />
                <span>6268642514</span>
              </a>
              <a
                href="mailto:VERMATAXCON121@GMAIL.COM"
                className="flex items-center gap-2 hover:text-[#10B981] transition-colors"
                data-testid="header-email-link"
              >
                <Mail size={14} />
                <span className="hidden sm:inline">VERMATAXCON121@GMAIL.COM</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_60388886-97e9-4082-ac83-90164f951064/artifacts/elf8n8pu_image.png"
              alt="Anku Tax Consultant"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-base font-medium transition-colors ${
                location.pathname === '/'
                  ? 'text-[#2D3C92]'
                  : 'text-slate-700 hover:text-[#2D3C92]'
              }`}
              data-testid="nav-home"
            >
              Home
            </Link>

            <Link
              to="/about"
              className={`text-base font-medium transition-colors ${
                location.pathname === '/about'
                  ? 'text-[#2D3C92]'
                  : 'text-slate-700 hover:text-[#2D3C92]'
              }`}
              data-testid="nav-about"
            >
              About
            </Link>

            {/* GST Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('gst')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className="flex items-center gap-1 text-base font-medium text-slate-700 hover:text-[#2D3C92] transition-colors"
                data-testid="nav-gst-dropdown"
              >
                GST Services
                <ChevronDown size={16} />
              </button>
              <AnimatePresence>
                {activeDropdown === 'gst' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-50"
                    data-testid="gst-dropdown-menu"
                  >
                    {gstServices.map((service, index) => (
                      <Link
                        key={index}
                        to={service.path}
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#2D3C92] transition-colors"
                        data-testid={`gst-service-${index}`}
                      >
                        {service.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Income Tax Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('income')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className="flex items-center gap-1 text-base font-medium text-slate-700 hover:text-[#2D3C92] transition-colors"
                data-testid="nav-income-dropdown"
              >
                Income Tax Services
                <ChevronDown size={16} />
              </button>
              <AnimatePresence>
                {activeDropdown === 'income' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-50"
                    data-testid="income-dropdown-menu"
                  >
                    {incomeTaxServices.map((service, index) => (
                      <Link
                        key={index}
                        to={service.path}
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#2D3C92] transition-colors"
                        data-testid={`income-service-${index}`}
                      >
                        {service.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/billing"
              className={`text-base font-medium transition-colors ${
                location.pathname === '/billing'
                  ? 'text-[#2D3C92]'
                  : 'text-slate-700 hover:text-[#2D3C92]'
              }`}
              data-testid="nav-billing"
            >
              Billing
            </Link>

            <Link
              to="/pricing"
              className={`text-base font-medium transition-colors ${
                location.pathname === '/pricing'
                  ? 'text-[#2D3C92]'
                  : 'text-slate-700 hover:text-[#2D3C92]'
              }`}
              data-testid="nav-pricing"
            >
              Pricing
            </Link>

            <Link
              to="/contact"
              className="bg-[#10B981] text-white hover:bg-[#047857] px-6 py-2.5 rounded-full font-medium transition-all shadow-md hover:shadow-lg"
              data-testid="nav-contact-btn"
            >
              Contact Us
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-slate-200 py-4"
              data-testid="mobile-menu"
            >
              <div className="space-y-2">
                <Link
                  to="/"
                  className="block px-4 py-2 text-slate-700 hover:bg-slate-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-nav-home"
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  className="block px-4 py-2 text-slate-700 hover:bg-slate-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-nav-about"
                >
                  About
                </Link>
                <Link
                  to="/gst-services"
                  className="block px-4 py-2 text-slate-700 hover:bg-slate-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-nav-gst"
                >
                  GST Services
                </Link>
                <Link
                  to="/income-tax-services"
                  className="block px-4 py-2 text-slate-700 hover:bg-slate-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-nav-income"
                >
                  Income Tax Services
                </Link>
                <Link
                  to="/billing"
                  className="block px-4 py-2 text-slate-700 hover:bg-slate-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-nav-billing"
                >
                  Billing
                </Link>
                <Link
                  to="/pricing"
                  className="block px-4 py-2 text-slate-700 hover:bg-slate-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-nav-pricing"
                >
                  Pricing
                </Link>
                <Link
                  to="/contact"
                  className="block px-4 py-2 text-white bg-[#10B981] rounded-lg mx-4 text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-nav-contact"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;